import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { fetchSiteImages } from '../lib/portfolio';
import { SlotEditor } from '../components/admin/SlotEditor';
import { GalleryManager } from '../components/admin/GalleryManager';
import { CategoryManager } from '../components/admin/CategoryManager';
import { ReviewsManager } from '../components/admin/ReviewsManager';
import { TextsManager } from '../components/admin/TextsManager';

const SERVICES = [
  { slug: 'mobelrenovering', label: 'Möbelrenovering' },
  { slug: 'dukning', label: 'Bröllop & Fest' },
  { slug: 'skyltfonster', label: 'Skyltfönster & Butik' },
  { slug: 'design', label: 'Grafisk Design' },
];

const TABS = [
  { id: 'topp', label: 'Startsidans toppbilder' },
  { id: 'omslag', label: 'Tjänsternas omslag' },
  { id: 'portratt', label: 'Om mig-porträtt' },
  { id: 'portfolio', label: 'Portfolio' },
  { id: 'galleri', label: 'Galleri (startsidan)' },
  { id: 'recensioner', label: 'Recensioner' },
  { id: 'texter', label: 'Texter' },
];

// --- Behållare -------------------------------------------------------------
const Shell = ({ children }) => (
  <div className="bg-cv-base min-h-screen" style={{ padding: 'clamp(80px,12vw,120px) clamp(16px,4vw,32px) 80px' }}>
    <div className="max-w-[1000px] mx-auto">{children}</div>
  </div>
);

const Heading = ({ title }) => (
  <div className="flex flex-col items-center gap-3.5 text-center mb-10">
    <span className="text-xs tracking-[0.34em] uppercase text-cv-accent">Administration</span>
    <h1 className="m-0 font-serif font-medium text-cv-ink" style={{ fontSize: 'clamp(30px,5vw,42px)' }}>{title}</h1>
    <span className="w-14 h-px bg-cv-accent" />
  </div>
);

export const AdminPage = () => {
  const [session, setSession] = useState(null);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [signingIn, setSigningIn] = useState(false);

  const [tab, setTab] = useState('topp');
  const [serviceSlug, setServiceSlug] = useState('mobelrenovering');
  const [siteImages, setSiteImages] = useState(null);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setChecking(false);
      return;
    }
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setChecking(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  const loadSiteImages = useCallback(async () => {
    setSiteImages(await fetchSiteImages());
  }, []);

  useEffect(() => {
    if (session) loadSiteImages();
  }, [session, loadSiteImages]);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setAuthError('');
    setSigningIn(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) setAuthError('Felaktig e-post eller lösenord.');
    setSigningIn(false);
  };

  // 1) Supabase saknas
  if (!isSupabaseConfigured) {
    return (
      <Shell>
        <Heading title="Adminläge" />
        <div className="bg-cv-panel border border-cv-border" style={{ borderRadius: 'var(--cv-radius)', padding: 32 }}>
          <p className="m-0 mb-3 text-cv-ink2 leading-[1.7]">
            Supabase är inte konfigurerat ännu. Sätt miljövariablerna <code className="text-cv-accent">REACT_APP_SUPABASE_URL</code> och{' '}
            <code className="text-cv-accent">REACT_APP_SUPABASE_ANON_KEY</code> och deploya om.
          </p>
          <p className="m-0 text-cv-muted text-sm leading-[1.7]">Se <code className="text-cv-accent">SETUP-SUPABASE.md</code> för instruktioner.</p>
          <Link to="/" className="inline-block mt-5 text-[13px] tracking-[0.16em] uppercase text-cv-ink border-b border-cv-edge pb-1 no-underline hover:border-cv-ink">← Till hemsidan</Link>
        </div>
      </Shell>
    );
  }

  if (checking) return <Shell><p className="text-center text-cv-muted">Laddar…</p></Shell>;

  // 2) Inloggning
  if (!session) {
    return (
      <Shell>
        <Heading title="Logga in" />
        <form onSubmit={handleSignIn} className="max-w-[400px] mx-auto flex flex-col gap-4 bg-cv-panel border border-cv-border" style={{ borderRadius: 'var(--cv-radius)', padding: 32 }}>
          <p className="m-0 text-sm text-cv-muted leading-[1.6]">Logga in med de uppgifter du fått för att hantera hemsidans bilder.</p>
          <label className="flex flex-col gap-1.5 text-sm text-cv-muted">
            E-post
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="username" className="px-3.5 py-3 bg-cv-base border border-cv-edge rounded-cv text-cv-ink outline-none focus:border-cv-accent" />
          </label>
          <label className="flex flex-col gap-1.5 text-sm text-cv-muted">
            Lösenord
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" className="px-3.5 py-3 bg-cv-base border border-cv-edge rounded-cv text-cv-ink outline-none focus:border-cv-accent" />
          </label>
          {authError && <p className="m-0 text-sm text-red-700">{authError}</p>}
          <button type="submit" disabled={signingIn} className="mt-1 bg-cv-ink text-cv-base border-none cursor-pointer px-7 py-3.5 text-[13px] tracking-[0.18em] uppercase rounded-cv hover:bg-cv-ink2 transition-colors disabled:opacity-60">
            {signingIn ? 'Loggar in…' : 'Logga in'}
          </button>
          <Link to="/" className="text-center text-[13px] tracking-[0.16em] uppercase text-cv-muted no-underline hover:text-cv-ink">← Till hemsidan</Link>
        </form>
      </Shell>
    );
  }

  // 3) Inloggad – hanteringspanel
  const tabBtn = (active) =>
    `px-4 py-2.5 text-sm rounded-cv border cursor-pointer transition-colors whitespace-nowrap ${
      active ? 'bg-cv-ink text-cv-base border-cv-ink' : 'bg-cv-base text-cv-ink border-cv-edge hover:bg-cv-line'
    }`;

  return (
    <div className="bg-cv-base min-h-screen">
      {/* Topprad – alltid synlig */}
      <div className="sticky top-0 z-40 border-b border-cv-border flex items-center justify-between gap-4 flex-wrap px-4 sm:px-6 py-3.5" style={{ background: 'rgba(250,247,242,0.96)', backdropFilter: 'blur(12px)' }}>
        <span className="font-serif text-[20px] tracking-[0.12em] text-cv-ink">CHills visuella <span className="text-cv-accent text-sm tracking-[0.2em] uppercase">· Adminläge</span></span>
        <div className="flex items-center gap-3">
          <Link to="/" className="text-[12px] tracking-[0.14em] uppercase text-cv-ink border border-cv-edge rounded-cv px-4 py-2 no-underline hover:bg-cv-line transition-colors">← Till hemsidan</Link>
          <button onClick={() => supabase.auth.signOut()} className="text-[12px] tracking-[0.14em] uppercase text-cv-ink bg-transparent border border-cv-edge rounded-cv px-4 py-2 cursor-pointer hover:bg-cv-line transition-colors">Logga ut</button>
        </div>
      </div>

      <div className="max-w-[1000px] mx-auto px-4 sm:px-6 py-8">
        {/* Välkomstguide */}
        <div className="bg-cv-panel border border-cv-border mb-8" style={{ borderRadius: 'var(--cv-radius)', padding: 24 }}>
          <h2 className="m-0 mb-2 font-serif font-medium text-[24px] text-cv-ink">Välkommen!</h2>
          <p className="m-0 text-[15px] text-cv-muted leading-[1.7]">
            Här kan du själv byta ut och hantera <strong>alla bilder</strong> på hemsidan. Välj vad du vill ändra i menyn nedan.
            Alla ändringar syns direkt på sidan. Du kan alltid gå tillbaka till hemsidan uppe till höger.
          </p>
        </div>

        {/* Meny */}
        <div className="flex gap-2 flex-wrap mb-8 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className={tabBtn(tab === t.id)}>{t.label}</button>
          ))}
        </div>

        {/* Innehåll */}
        {!siteImages && !['portfolio', 'galleri', 'recensioner', 'texter'].includes(tab) ? (
          <p className="text-cv-muted">Laddar…</p>
        ) : (
          <>
            {tab === 'topp' && siteImages && (
              <div className="flex flex-col gap-6">
                <SlotEditor slot="hero_primary" label="Stora bilden (uppe till höger)" help="Den stora bilden högst upp på startsidan." aspect="4/3" image={siteImages.hero_primary} onChanged={loadSiteImages} />
                <SlotEditor slot="hero_secondary" label="Lilla bilden (nere till vänster)" help="Den mindre bilden som överlappar den stora på startsidan." aspect="1/1" image={siteImages.hero_secondary} onChanged={loadSiteImages} />
              </div>
            )}

            {tab === 'omslag' && siteImages && (
              <div className="flex flex-col gap-6">
                <SlotEditor slot="thumb_mobelrenovering" label="Omslag: Möbelrenovering" aspect="4/3" image={siteImages.thumb_mobelrenovering} onChanged={loadSiteImages} />
                <SlotEditor slot="thumb_dukning" label="Omslag: Bröllop & Fest" aspect="4/3" image={siteImages.thumb_dukning} onChanged={loadSiteImages} />
                <SlotEditor slot="thumb_skyltfonster" label="Omslag: Skyltfönster & Butik" aspect="4/3" image={siteImages.thumb_skyltfonster} onChanged={loadSiteImages} />
                <SlotEditor slot="thumb_design" label="Omslag: Grafisk Design" aspect="4/3" image={siteImages.thumb_design} onChanged={loadSiteImages} />
              </div>
            )}

            {tab === 'portratt' && siteImages && (
              <SlotEditor slot="about_portrait" label="Porträtt på Om mig-sidan" help="Bilden på dig på sidan Om mig." aspect="3/4" image={siteImages.about_portrait} onChanged={loadSiteImages} />
            )}

            {tab === 'portfolio' && (
              <div className="flex flex-col gap-6">
                <div className="flex gap-2 flex-wrap">
                  {SERVICES.map((s) => (
                    <button key={s.slug} onClick={() => setServiceSlug(s.slug)} className={tabBtn(serviceSlug === s.slug)}>{s.label}</button>
                  ))}
                </div>
                <CategoryManager key={serviceSlug} category={serviceSlug} label={SERVICES.find((s) => s.slug === serviceSlug).label} />
              </div>
            )}

            {tab === 'galleri' && <GalleryManager />}

            {tab === 'recensioner' && <ReviewsManager />}

            {tab === 'texter' && <TextsManager />}
          </>
        )}
      </div>
    </div>
  );
};
